import React, { useState } from "react";
import { Mail, MessageSquare, Bell } from "lucide-react";

const SECTIONS = [
  {
    id: "documentEvents",
    title: "Document Events",
    items: [
      { key: "signed", label: "When a document is signed" },
      { key: "verified", label: "When a document is verified" },
      { key: "rejected", label: "When signature is rejected" },
      { key: "expiring", label: "When certificate is expiring" },
      { key: "documentRejected", label: "When document is rejected" },
      { key: "dailySummary", label: "Daily summary of activities" },
    ],
  },
  {
    id: "securityAlerts",
    title: "Security Alerts",
    items: [
      { key: "newSignIn", label: "New sign-in on unrecognized device" },
      { key: "suspiciousActivity", label: "Suspicious activity detected" },
      { key: "passwordChanged", label: "Password changed" },
      { key: "mfaDevice", label: "MFA device added or removed" },
      { key: "certificateRevoked", label: "Certificate revoked" },
      { key: "securityLogin", label: "Security alerts & login activity" },
    ],
  },
  {
    id: "systemAlerts",
    title: "System Alerts",
    items: [
      { key: "maintenance", label: "System maintenance" },
      { key: "downtime", label: "Service downtime" },
      { key: "updates", label: "Feature updates" },
      { key: "storageQuota", label: "Storage quota exceeded" },
      { key: "backupCompleted", label: "Backup completed" },
    ],
  },
];

const INITIAL_PREFS = {
  email: {
    documentEvents: { signed: true, verified: true, rejected: true, expiring: true, documentRejected: true, dailySummary: true },
    securityAlerts: { newSignIn: true, suspiciousActivity: true, passwordChanged: true, mfaDevice: true, certificateRevoked: true, securityLogin: true },
    systemAlerts: { maintenance: true, downtime: true, updates: true, storageQuota: false, backupCompleted: true },
  },
  sms: {
    documentEvents: { signed: true, verified: true, rejected: true, expiring: true, documentRejected: true, dailySummary: false },
    securityAlerts: { newSignIn: true, suspiciousActivity: true, passwordChanged: true, mfaDevice: false, certificateRevoked: false, securityLogin: true },
    systemAlerts: { maintenance: false, downtime: true, updates: false, storageQuota: false, backupCompleted: false },
  },
  inapp: {
    documentEvents: { signed: true, verified: true, rejected: true, expiring: true, documentRejected: true, dailySummary: true },
    securityAlerts: { newSignIn: true, suspiciousActivity: true, passwordChanged: true, mfaDevice: true, certificateRevoked: true, securityLogin: true },
    systemAlerts: { maintenance: true, downtime: true, updates: true, storageQuota: true, backupCompleted: true },
  },
};

const TABS = [
  { id: "email", label: "Email Notifications" },
  { id: "sms", label: "SMS Notifications" },
  { id: "inapp", label: "In-App Notifications" },
];

const ToggleSection = ({ section, values, onToggle, styles }) => (
  <div style={styles.sectionColumn}>
    <h3 style={styles.sectionTitle}>{section.title}</h3>
    {section.items.map((item, idx) => {
      const isOn = values[item.key];
      const isLast = idx === section.items.length - 1;
      return (
        <div key={item.key} style={{ ...styles.notificationItem, ...(isLast ? styles.lastItem : {}) }}>
          <span>{item.label}</span>
          <label style={styles.toggle}>
            <input
              type="checkbox"
              checked={isOn}
              onChange={() => onToggle(section.id, item.key)}
              style={styles.toggleInput}
            />
            <span style={{ ...styles.slider, ...(isOn ? styles.toggleActive : {}) }}>
              <span style={{ ...styles.sliderBefore, ...(isOn ? styles.toggleActiveBefore : {}) }} />
            </span>
          </label>
        </div>
      );
    })}
  </div>
);

const MyNotifications = () => {
  const [activeTab, setActiveTab] = useState("email");
  const [prefs, setPrefs] = useState(INITIAL_PREFS);

  const [startTime, setStartTime] = useState("10:00");
  const [endTime, setEndTime] = useState("07:00");


  const styles = {
    page: {
      minHeight: "100vh",
      background: "#ffffff",
      padding: "0px",
      fontFamily: "Inter, sans-serif",
    },
    container: {
      maxWidth: "1200px",
      margin: "0 auto",
    },
    breadcrumb: {
      fontSize: "12px",
      color: "#64748b",
      marginBottom: "16px",
    },
    header: {
      marginBottom: "24px",
    },
    title: {
      fontSize: "28px",
      fontWeight: "700",
      color: "#111827",
      margin: "0 0 4px 0",
    },
    subtitle: {
      fontSize: "14px",
      color: "#64748b",
      margin: "0",
    },
    tabs: {
      display: "flex",
      gap: "24px",
      borderBottom: "1px solid #e5e7eb",
      marginBottom: "24px",
    },
    tab: {
      padding: "12px 0",
      fontSize: "14px",
      color: "#64748b",
      border: "none",
      borderBottom: "3px solid transparent",
      background: "none",
      cursor: "pointer",
      fontWeight: "500",
      transition: "all 0.2s",
      marginBottom: "-1px",
    },
    tabActive: {
      color: "#111827",
      borderBottom: "3px solid #2563eb",
    },
    mainContent: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr 1fr",
      gap: "24px",
      marginBottom: "24px",
    },
    sectionColumn: {
      background: "#fff",
      border: "1px solid #e5e7eb",
      borderRadius: "12px",
      padding: "20px",
      display: "flex",
      flexDirection: "column",
      gap: "0",
    },
    sectionTitle: {
      fontSize: "14px",
      fontWeight: "600",
      color: "#111827",
      marginBottom: "16px",
      paddingBottom: "12px",
      borderBottom: "1px solid #e5e7eb",
    },
    notificationItem: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "12px 0",
      borderBottom: "1px solid #eef2f7",
      fontSize: "13px",
      color: "#334155",
    },
    lastItem: {
      borderBottom: "none",
    },
    toggle: {
      position: "relative",
      width: "48px",
      height: "28px",
    },
    toggleInput: {
      display: "none",
    },
    slider: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "#cbd5e1",
      borderRadius: "999px",
      cursor: "pointer",
      transition: "0.25s",
    },
    sliderBefore: {
      content: '""',
      position: "absolute",
      width: "22px",
      height: "22px",
      left: "3px",
      top: "3px",
      background: "#fff",
      borderRadius: "50%",
      transition: "0.25s",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.18)",
    },
    toggleActive: {
      background: "#5b3df5",
    },
    toggleActiveBefore: {
      transform: "translateX(20px)",
    },
    channelsSection: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "24px",
      marginBottom: "24px",
    },
    channels: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr 1fr",
      gap: "16px",
    },
    channelCard: {
      background: "#fff",
      border: "1px solid #e5e7eb",
      borderRadius: "12px",
      padding: "16px",
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: "16px",
    },
    channelIcon: {
      fontSize: "24px",
      marginBottom: "12px",
    },
    channelName: {
      fontSize: "13px",
      fontWeight: "600",
      color: "#111827",
      marginBottom: "8px",
    },
    channelInfo: {
      fontSize: "12px",
      color: "#64748b",
      marginBottom: "8px",
    },
    badge: {
      display: "inline-block",
      background: "#dcfce7",
      color: "#16a34a",
      padding: "4px 8px",
      borderRadius: "4px",
      fontSize: "11px",
      fontWeight: "600",
    },
    quietHoursBox: {
      background: "#fff",
      border: "1px solid #e5e7eb",
      borderRadius: "12px",
      padding: "16px",
      display: "inline-block",
      width: "100%",
    },
    quietHoursContent: {
      marginBottom: "0",
    },
    timeInputGroup: {
      display: "flex",
      gap: "16px",
      marginBottom: "0",
    },
    timeInput: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      gap: "4px",
    },
    timeLabel: {
      fontSize: "12px",
      fontWeight: "600",
      color: "#1e293b",
    },
    input: {
      padding: "8px 10px",
      border: "1px solid #d7dce3",
      borderRadius: "8px",
      fontSize: "12px",
      color: "#334155",
      outline: "none",
      width: "100%",
    },
    footer: {
      fontSize: "12px",
      color: "#64748b",
      marginBottom: "16px",
    },
    buttonSection: {
      display: "flex",
      justifyContent: "flex-end",
    },
    saveButton: {
      background: "#5b3df5",
      color: "#fff",
      border: "none",
      padding: "10px 24px",
      borderRadius: "8px",
      fontSize: "14px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "0.2s",
    },
    saveButtonHover: {
      background: "#4c2fd9",
    },
  };

  const timeOptions = Array.from({ length: 24 }, (_, i) => {
    const h = i % 12 === 0 ? 12 : i % 12;
    const ampm = i < 12 ? "AM" : "PM";
    const val = `${String(i).padStart(2, "0")}:00`;
    return { label: `${h}:00 ${ampm}`, value: val };
  });

  const handleToggle = (sectionId, key) => {
    setPrefs((prev) => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        [sectionId]: {
          ...prev[activeTab][sectionId],
          [key]: !prev[activeTab][sectionId][key],
        },
      },
    }));
  };


  return (
    <div style={styles.page}>
      <div style={styles.container}>
        
        
        

        <div style={styles.tabs}>
          {TABS.map((tab) => (
            <button
              key={tab.id}
              style={{ ...styles.tab, ...(activeTab === tab.id ? styles.tabActive : {}) }}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div style={styles.mainContent}>
          {SECTIONS.map((section) => (
            <ToggleSection
              key={section.id}
              section={section}
              values={prefs[activeTab][section.id]}
              onToggle={handleToggle}
              styles={styles}
            />
          ))}
        </div>


        <div style={styles.channelsSection}>
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: "12px", padding: "24px 28px" }}>
            <h3 style={{ fontSize: "14px", fontWeight: "600", color: "#111827", marginBottom: "16px" }}>
              Notification Channels
            </h3>
            <div style={styles.channels}>
              <div style={styles.channelCard}>
                <div style={{ alignSelf: "flex-start" }}>
                  <Mail size={24} color="#ef4444" />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <div style={styles.channelName}>Email</div>
                  <div style={styles.channelInfo}>admin@intrust.com</div>
                  <div style={styles.badge}>Verified</div>
                </div>
              </div>
              <div style={styles.channelCard}>
                <div style={{ alignSelf: "flex-start" }}>
                  <MessageSquare size={24} color="#5b3df5" />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <div style={styles.channelName}>SMS</div>
                  <div style={styles.channelInfo}>+91 98765 43310</div>
                  <div style={styles.badge}>Verified</div>
                </div>
              </div>
              <div style={styles.channelCard}>
                <div style={{ alignSelf: "flex-start" }}>
                  <Bell size={24} color="#5b3df5" />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <div style={styles.channelName}>In-App</div>
                  <div style={styles.channelInfo}>Enabled</div>
                  <div style={styles.badge}>All devices</div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <div style={styles.quietHoursBox}>
              <h3 style={{ fontSize: "14px", fontWeight: "600", color: "#111827", marginBottom: "4px" }}>
                Quiet Hours
              </h3>
              <p style={{ fontSize: "12px", color: "#94a3b8", marginBottom: "12px" }}>Please pause notifications during this time.</p>
              <div style={styles.quietHoursContent}>
                <div style={styles.timeInputGroup}>
                  <div style={styles.timeInput}>
                    <label style={styles.timeLabel}>Start Time</label>
                    <select value={startTime} onChange={(e) => setStartTime(e.target.value)} style={styles.input}>
                      {timeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div style={styles.timeInput}>
                    <label style={styles.timeLabel}>End Time</label>
                    <select value={endTime} onChange={(e) => setEndTime(e.target.value)} style={styles.input}>
                      {timeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={styles.footer}>
          You will still receive critical security alerts during quiet hours.
        </div>

        <div style={styles.buttonSection}>
          <button style={styles.saveButton} onMouseOver={(e) => e.target.style.background = "#4c2fd9"} onMouseOut={(e) => e.target.style.background = "#5b3df5"}>
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  );
};

export default MyNotifications;